import { ArrowRight, Check, ChevronRight, Gavel, Inbox, ShieldAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { api, type PolicyDecision, type DomainDetail, type DomainLock } from '../../../api';
import type { DomainAuditEntry } from '../../../api/areas/core';
import { Badge } from '../../../components/Badge';
import { Callout, DataTable, Pager, Section, type Column } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { CorrelationLink } from '../../../components/Ids';
import { KeyValue } from '../../../components/KeyValue';
import { EmptyState, Loading } from '../../../components/States';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { isCode, useLabels } from '../../../lib/labels';
import { useApi } from '../../../lib/useApi';
import { SyncErrorCode } from '../../compliance/vnnic/shared';
import { ActorName, RegistryRef, TxRef, useVocab } from '../refs';
import { refusedCommand } from '../registration';
import { RefusalText } from '../RegistrationStop';
import { EppTab } from './CoreTabs';
import { ABUSE, COMMAND_STATE, DISPUTE, DOSSIER, humanize, LIFECYCLE, LIFECYCLE_PATH, POLICY_OUTCOME, StatusBadge, useStatusLabel, VNNIC_SYNC, VNNIC_TX } from '../status';
import { SystemReason } from '@/components/SystemReason';

type TabProps = { detail: DomainDetail };

type Lookup = (key: string, options?: Record<string, unknown>) => string;

/**
 * A required action or block reason of a policy decision (`{ code, ...details }`) as a sentence:
 * the code and every known detail (document type, accepted methods, policy, lock type) translated.
 */
function useDecisionItem(kind: 'requiredActions' | 'blockReasons'): (item: unknown) => string {
  const { t } = useTranslation('domains');
  const tr = t as unknown as Lookup;
  const vocab = useVocab();
  const L = useLabels();
  return (item) => {
    const o: Record<string, unknown> = item && typeof item === 'object' ? (item as Record<string, unknown>) : { code: item };
    const code = [o.code, o.action, o.reason, o.type].find((v): v is string => typeof v === 'string') ?? '';
    const head = isCode(code) ? tr(`compliance.${kind}.${code}`, { defaultValue: humanize(code) }) : code;
    const text = (k: string) => (typeof o[k] === 'string' ? (o[k] as string) : null);
    const doc = text('document_type') ?? text('purpose');
    const policy = text('policy');
    const lock = text('lock_type');
    const methods = Array.isArray(o.accepted_methods) ? o.accepted_methods.filter((m): m is string => typeof m === 'string') : [];
    const details = [
      doc ? vocab('documentType', doc) : null,
      lock ? vocab('lock', lock) : null,
      methods.length ? t('compliance.acceptedMethods', { methods: methods.map((m) => vocab('method', m)).join(', ') }) : null,
      policy ? t('compliance.underPolicy', { policy: L.policy(policy) }) : null,
    ].filter(Boolean);
    return details.length ? `${head}: ${details.join(' · ')}` : head;
  };
}

/** Loading / error states inside a flush Section. */
function Pad({ children }: { children: ReactNode }) {
  return <div className="px-5 pb-5">{children}</div>;
}

const code = 'rounded bg-muted px-1.5 py-0.5 font-mono text-xs';

export function LifecycleTab({ detail }: TabProps) {
  const { t } = useTranslation('domains');
  const vocab = useVocab();
  const label = useStatusLabel();
  const d = detail.domain;
  const currentIndex = LIFECYCLE_PATH.indexOf(d.lifecycle_state);
  return (
    <div className="grid grid-cols-1 gap-6">
      <Section title={t('lifecycle.state')} description={t('lifecycle.stateDescription')}>
        <div className="grid grid-cols-1 gap-5">
          <ol className="flex flex-wrap items-center gap-y-2" aria-label={t('lifecycle.pathLabel')}>
            {LIFECYCLE_PATH.map((state, i) => {
              const done = currentIndex >= 0 && i < currentIndex;
              const current = i === currentIndex;
              return (
                <li key={state} aria-current={current ? 'step' : undefined} className="flex items-center">
                  {i > 0 ? <ChevronRight className="mx-1 size-4 shrink-0 text-muted-foreground/60" aria-hidden="true" /> : null}
                  <span
                    title={state}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium',
                      current && 'border-primary bg-primary text-primary-foreground',
                      done && 'border-success/30 bg-success-soft text-success',
                      !current && !done && 'text-muted-foreground',
                    )}
                  >
                    {done ? <Check className="size-3" aria-hidden="true" /> : null}
                    {label(LIFECYCLE, state)}
                  </span>
                </li>
              );
            })}
          </ol>
          {currentIndex < 0 ? (
            <p className="flex flex-wrap items-center gap-2 text-sm">
              {t('lifecycle.offPath')} <StatusBadge map={LIFECYCLE} value={d.lifecycle_state} />
            </p>
          ) : null}
          <KeyValue
            columns={2}
            rows={[
              [t('lifecycle.changed'), d.lifecycle_changed_at ? `${formatDateTime(d.lifecycle_changed_at)} (${formatRelative(d.lifecycle_changed_at)})` : '—'],
              [t('lifecycle.transferState'), vocab('transferState', d.transfer_state)],
              [t('lifecycle.dnsState'), vocab('dnsState', d.dns_state)],
              [t('lifecycle.recordVersion'), d.version],
            ]}
          />
        </div>
      </Section>
      <Section title={t('lifecycle.keyDates')}>
        <KeyValue
          columns={2}
          rows={[
            [t('lifecycle.createdRegistrar'), formatDateTime(d.customer_created_at ?? d.created_at)],
            [t('lifecycle.createdRegistry'), formatDateTime(d.registry_created_at)],
            [t('lifecycle.registryExpiry'), d.registry_expires_at ? `${formatDateTime(d.registry_expires_at)} (${formatRelative(d.registry_expires_at)})` : '—'],
            [t('lifecycle.paidThrough'), d.customer_expires_at ? `${formatDateTime(d.customer_expires_at)} (${formatRelative(d.customer_expires_at)})` : '—'],
            [t('lifecycle.autoRenew'), d.auto_renew ? t('lifecycle.autoRenewOn', { years: d.auto_renew_period }) : t('lifecycle.off')],
            [t('lifecycle.authInfo'), formatDateTime(d.auth_info_set_at ?? null)],
          ]}
        />
        <Callout className="mt-5">{t('lifecycle.note')}</Callout>
      </Section>
    </div>
  );
}

export function RegistryTab({ detail }: TabProps) {
  const { t } = useTranslation('domains');
  const vocab = useVocab();
  const d = detail.domain;
  const txs = useApi((signal) => api.listDomainRegistryTransactions(d.id, { signal }), [d.id]);
  const unknown = txs.status === 'success' ? txs.data.items.filter((x) => x.state === 'UNKNOWN' || x.state === 'RECONCILING' || x.state === 'MANUAL_REVIEW').length : 0;
  // Only the latest command (owner 2026-10-07): an older refusal the domain has since got past
  // misleads; every command stays in "Lệnh gửi registry" below.
  const latest = txs.status === 'success' ? txs.data.items.filter((x) => x.error_class !== 'ABANDONED').sort((a, b) => (b.sent_at ?? b.started_at).localeCompare(a.sent_at ?? a.started_at))[0] : undefined;
  const stop = detail.registration?.state === 'MANUAL_REVIEW' ? detail.registration : null;

  const lockColumns: Column<DomainLock>[] = [
    {
      key: 'lock',
      header: t('registry.lock'),
      cell: (l) => (
        <Badge tone={l.lock_type === 'REGISTRAR_LOCK' || l.lock_type === 'TRANSFER_LOCK' ? 'neutral' : 'warn'} title={l.lock_type}>
          {vocab('lock', l.lock_type)}
        </Badge>
      ),
    },
    { key: 'reason', header: t('registry.reason'), cell: (l) => <SystemReason reason={l.reason} /> },
    { key: 'case', header: t('registry.case'), cell: (l) => <span className="font-mono text-xs">{l.case_reference ?? '—'}</span> },
    { key: 'applied', header: t('registry.applied'), className: 'whitespace-nowrap', cell: (l) => formatDateTime(l.applied_at) },
    { key: 'release', header: t('registry.autoRelease'), className: 'whitespace-nowrap', cell: (l) => formatDateTime(l.auto_release_at ?? null) },
  ];

  return (
    <div className="grid grid-cols-1 gap-6">
      <Section title={t('registry.object')}>
        <KeyValue
          columns={2}
          rows={[
            [t('registry.registry'), <RegistryRef key="r" code={d.registry} name={d.registry_name} />],
            [t('registry.tld'), `.${d.tld} · ${vocab('tldClass', d.tld_class)}`],
            [
              t('registry.statuses'),
              detail.registry_statuses.length ? (
                <span key="st" className="flex flex-wrap gap-1">
                  {detail.registry_statuses.map((s) => (
                    <code key={s.status} className={code} title={`${vocab('eppStatus', s.status)} · ${t('registry.statusSource', { source: s.source, at: formatDateTime(s.observed_at) })}`}>
                      {s.status}
                    </code>
                  ))}
                </span>
              ) : (
                t('registry.none')
              ),
            ],
            [t('registry.expiry'), formatDateTime(d.registry_expires_at)],
            [
              t('registry.unresolved'),
              txs.status !== 'success' ? (
                '…'
              ) : unknown > 0 ? (
                <Badge key="u" tone="serious">
                  {t('registry.unresolvedCount', { count: unknown })}
                </Badge>
              ) : (
                <Badge key="u" tone="ok">
                  {t('registry.none')}
                </Badge>
              ),
            ],
            [
              t('registry.lastRefusal'),
              txs.status !== 'success' ? (
                '…'
              ) : latest ? (
                <span key="rf" className="flex flex-col gap-0.5 text-sm">
                  <span className="flex flex-wrap items-center gap-2">
                    <StatusBadge map={COMMAND_STATE} value={latest.state} />
                    <TxRef id={latest.id} operation={latest.operation} />
                    {latest.result_code !== null ? <code className={code}>{latest.result_code}</code> : null}
                    <span className="text-xs text-muted-foreground">{formatDateTime(latest.started_at)}</span>
                  </span>
                  {latest.state === 'REJECTED' ? <RefusalText command={refusedCommand(latest, detail.registration?.refused_command)} /> : null}
                </span>
              ) : (
                <span key="rf" className="text-muted-foreground">{t('registry.noRefusal')}</span>
              ),
            ],
          ]}
        />
        {stop ? (
          <div className="mt-4">
            <Callout>
              <span className="font-medium">{t('stop.title')}:</span> {stop.reason}{' '}
              <Link to={`/domains/${encodeURIComponent(d.ascii_name)}`} className="text-primary hover:underline">
                {t('stop.openOverview')}
              </Link>
            </Callout>
          </div>
        ) : null}
      </Section>

      {detail.locks.length ? (
        <Section flush title={t('registry.locks')}>
          <DataTable columns={lockColumns} rows={detail.locks} rowKey={(l) => l.id} />
        </Section>
      ) : null}

      <EppTab detail={detail} />
    </div>
  );
}

export function ComplianceTab({ detail }: TabProps) {
  const { t } = useTranslation('domains');
  const tr = t as unknown as Lookup;
  const vocab = useVocab();
  const L = useLabels();
  const requiredAction = useDecisionItem('requiredActions');
  const blockReason = useDecisionItem('blockReasons');
  const d = detail.domain;
  const decisions = useApi((signal) => api.listDomainComplianceDecisions(d.id, { signal }), [d.id]);
  const cases = useApi((signal) => api.listAbuseCases({ domain: d.ascii_name, limit: 50 }, { signal }), [d.ascii_name]);
  const disputes = useApi((signal) => api.listDisputes({ domain_id: d.id, limit: 50 }, { signal }), [d.id]);

  const decisionColumns: Column<PolicyDecision>[] = [
    {
      key: 'decided',
      header: t('compliance.decided'),
      cell: (x) => (
        <div className="flex flex-col items-start gap-0.5">
          <span className="whitespace-nowrap">{formatDateTime(x.decided_at)}</span>
          {x.correlation_id ? <CorrelationLink id={x.correlation_id} label={t('compliance.correlation')} /> : null}
        </div>
      ),
    },
    { key: 'op', header: t('compliance.operation'), cell: (x) => vocab('operation', x.operation) },
    { key: 'outcome', header: t('compliance.outcome'), cell: (x) => <StatusBadge map={POLICY_OUTCOME} value={x.outcome} /> },
    {
      key: 'actions',
      header: t('compliance.actions'),
      className: 'min-w-52 whitespace-normal',
      cell: (x) => {
        const all = [...x.required_actions.map(requiredAction), ...x.block_reasons.map(blockReason)];
        return all.length ? (
          <ul className="list-disc space-y-0.5 pl-4 text-xs">
            {all.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
    {
      key: 'policies',
      header: t('compliance.policies'),
      className: 'min-w-52 whitespace-normal',
      cell: (x) => (
        <ul className="flex flex-col gap-0.5 text-xs">
          {x.evaluated_policies.map((p) => {
            // Stored decisions name the policy `policy`; older rows used `policy_key`.
            const key = p.policy_key ?? (p as { policy?: string }).policy;
            return <li key={`${key}@${p.version}`}>{L.policy(key, p.version)}</li>;
          })}
        </ul>
      ),
    },
  ];

  const rowLink = 'flex items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none';

  return (
    <div className="grid grid-cols-1 gap-6">
      <Section flush title={t('compliance.decisions')} description={t('compliance.decisionsDescription')}>
        {decisions.status === 'loading' ? (
          <Pad>
            <Loading rows={2} />
          </Pad>
        ) : null}
        {decisions.status === 'error' ? (
          <Pad>
            <DataError error={decisions.error} onRetry={decisions.reload} />
          </Pad>
        ) : null}
        {decisions.status === 'success' ? (
          <DataTable columns={decisionColumns} rows={decisions.data.items} rowKey={(x) => x.id} empty={<EmptyState title={t('compliance.noDecisions')} />} />
        ) : null}
      </Section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section
          flush
          title={t('compliance.abuse')}
          actions={
            <Button asChild variant="ghost" size="sm">
              <Link to={`/compliance/icann/abuse?domain=${encodeURIComponent(d.ascii_name)}`}>
                {t('compliance.openAbuse')} <ArrowRight />
              </Link>
            </Button>
          }
        >
          {cases.status === 'loading' ? (
            <Pad>
              <Loading />
            </Pad>
          ) : null}
          {cases.status === 'error' ? (
            <Pad>
              <DataError error={cases.error} onRetry={cases.reload} />
            </Pad>
          ) : null}
          {cases.status === 'success' && cases.data.items.length === 0 ? (
            <Pad>
              <EmptyState title={t('compliance.noAbuse')} icon={ShieldAlert} />
            </Pad>
          ) : null}
          {cases.status === 'success' && cases.data.items.length > 0 ? (
            <ul className="divide-y border-t">
              {cases.data.items.map((c) => (
                <li key={c.id}>
                  <Link to={`/compliance/icann/abuse/${encodeURIComponent(c.id)}`} className={rowLink}>
                    <StatusBadge map={ABUSE} value={c.status} />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <strong className="font-mono text-sm">{c.case_number}</strong>
                      <span className="text-xs text-muted-foreground">
                        {t('compliance.abuseLine', {
                          category: tr(`compliance.categories.${c.category}`, { defaultValue: humanize(c.category) }),
                          priority: tr(`compliance.priorities.${c.priority}`, { defaultValue: humanize(c.priority).toLowerCase() }),
                          when: formatRelative(c.opened_at),
                        })}
                      </span>
                    </span>
                    <ChevronRight className="size-4 text-muted-foreground" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
        </Section>

        <Section
          flush
          title={t('compliance.disputes')}
          actions={
            <Button asChild variant="ghost" size="sm">
              <Link to="/compliance/icann/disputes">
                {t('compliance.openDisputes')} <ArrowRight />
              </Link>
            </Button>
          }
        >
          {disputes.status === 'loading' ? (
            <Pad>
              <Loading />
            </Pad>
          ) : null}
          {disputes.status === 'error' ? (
            <Pad>
              <DataError error={disputes.error} onRetry={disputes.reload} />
            </Pad>
          ) : null}
          {disputes.status === 'success' && disputes.data.items.length === 0 ? (
            <Pad>
              <EmptyState title={t('compliance.noDisputes')} icon={Gavel} />
            </Pad>
          ) : null}
          {disputes.status === 'success' && disputes.data.items.length > 0 ? (
            <ul className="divide-y border-t">
              {disputes.data.items.map((x) => (
                <li key={x.id}>
                  <Link to={`/compliance/icann/disputes/${encodeURIComponent(x.id)}`} className={rowLink}>
                    <StatusBadge map={DISPUTE} value={x.status} />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <strong className="text-sm">
                        {tr(`compliance.disputeTypes.${x.dispute_type}`, { defaultValue: humanize(x.dispute_type) })}
                        {x.provider ? ` · ${x.provider}` : ''}
                      </strong>
                      <span className="text-xs text-muted-foreground">
                        <code className="font-mono">{x.case_reference}</code> ·{' '}
                        {t('compliance.lockLine', { lock: vocab('lock', x.lock_type), state: x.lock_active ? t('compliance.lockActive') : t('compliance.lockReleased') })}
                      </span>
                    </span>
                    <ChevronRight className="size-4 text-muted-foreground" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
        </Section>
      </div>
    </div>
  );
}

/** The domain's own VNNIC dossier sync state (read-only; the VNNIC pages have the full view). */
export function VnnicSyncState({ domainId }: { domainId: string }) {
  const { t } = useTranslation('domains');
  const tr = t as unknown as Lookup;
  const state = useApi((signal) => api.getDomainVnnic(domainId, { signal }), [domainId]);
  if (state.status === 'loading') return <Loading label={t('vnnic.syncLoading')} rows={2} />;
  if (state.status === 'error') return <DataError error={state.error} onRetry={state.reload} title={t('vnnic.syncFailed')} />;
  const s = state.data.sync;
  if (!s) return <p className="text-sm text-muted-foreground">{t('vnnic.noSync')}</p>;
  const last = s.last_transaction;
  return (
    <div className="grid grid-cols-1 gap-4">
      <KeyValue
        columns={3}
        rows={[
          [t('vnnic.syncStatus'), <StatusBadge key="s" map={VNNIC_SYNC} value={s.status} />],
          [t('vnnic.syncOperation'), tr(`vnnic.operations.${s.operation}`, { defaultValue: humanize(s.operation) })],
          [t('vnnic.dossierVersion'), t('common.version', { n: s.dossier_version })],
          [t('vnnic.attempts'), String(s.attempts)],
          [t('vnnic.changedAt'), formatDateTime(s.status_changed_at)],
          s.external_reference ? [t('vnnic.reference'), <code key="r" className={code}>{s.external_reference}</code>] : null,
          s.last_error_code ? [t('vnnic.errorCode'), <SyncErrorCode key="e" code={s.last_error_code} />] : null,
          s.next_attempt_at ? [t('vnnic.nextAttempt'), formatDateTime(s.next_attempt_at)] : null,
        ]}
      />
      {last ? (
        <div className="rounded-lg border p-3">
          <div className="mb-2 text-sm font-semibold">{t('vnnic.lastAttempt')}</div>
          <KeyValue
            columns={3}
            rows={[
              [t('vnnic.attemptResult'), <StatusBadge key="s" map={VNNIC_TX} value={last.status} />],
              [t('vnnic.submittedAt'), formatDateTime(last.submitted_at)],
              [t('vnnic.completedAt'), formatDateTime(last.completed_at)],
              last.error_code ? [t('vnnic.errorCode'), <SyncErrorCode key="e" code={last.error_code} />] : null,
              last.error_message ? [t('vnnic.errorMessage'), <span key="m">{last.error_message}</span>] : null,
            ]}
          />
        </div>
      ) : null}
    </div>
  );
}

export function VnnicTab({ detail }: TabProps) {
  const { t } = useTranslation('domains');
  const d = detail.domain;
  const vn = d.tld === 'vn' || d.tld.endsWith('.vn');
  return (
    <div className="grid grid-cols-1 gap-6">
      <Section title={t('vnnic.title')} description={t('vnnic.description')}>
        <KeyValue
          columns={2}
          rows={[
            [t('vnnic.program'), vn ? t('vnnic.programVn') : t('vnnic.programIntl')],
            [
              t('vnnic.dossier'),
              <span key="d" className="flex flex-wrap items-center gap-2">
                <StatusBadge map={DOSSIER} value={detail.dossier?.status ?? d.dossier_state} />
                {detail.dossier ? (
                  <Link to={`/dossiers/${encodeURIComponent(detail.dossier.id)}`} className="text-sm text-primary hover:underline">
                    {t('vnnic.openDossier')}
                  </Link>
                ) : null}
              </span>,
            ],
          ]}
        />
        <p className="mt-5 text-sm text-muted-foreground">{t('vnnic.note')}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {vn ? (
            <Button asChild size="sm">
              <Link to="/compliance/vnnic/dossier-sync">
                {t('vnnic.dossierSync')} <ArrowRight />
              </Link>
            </Button>
          ) : (
            <Button asChild size="sm">
              <Link to="/compliance/vnnic/international-reports">
                {t('vnnic.intlReports')} <ArrowRight />
              </Link>
            </Button>
          )}
          <Button asChild variant="outline" size="sm">
            <Link to="/compliance/vnnic/coverage">{t('vnnic.coverage')}</Link>
          </Button>
        </div>
      </Section>
      {vn ? (
        <Section title={t('vnnic.sync')} description={t('vnnic.syncDescription')}>
          <VnnicSyncState domainId={d.id} />
        </Section>
      ) : null}
    </div>
  );
}

const AUDIT_LIMIT = 50;

export function AuditTab({ detail }: TabProps) {
  const { t } = useTranslation('domains');
  const L = useLabels();
  const d = detail.domain;
  const [pages, setPages] = useState<string[]>([]);
  const before = pages[pages.length - 1];
  const audit = useApi((signal) => api.listDomainAudit(d.id, { limit: AUDIT_LIMIT, before }, { signal }), [d.id, before]);
  const outcomes = t('audit.outcomes', { returnObjects: true }) as Record<string, string>;

  const columns: Column<DomainAuditEntry>[] = [
    {
      key: 'when',
      header: t('audit.when'),
      cell: (a) => (
        <div className="flex flex-col items-start gap-0.5">
          <span className="whitespace-nowrap">{formatDateTime(a.created_at)}</span>
          {a.correlation_id ? <CorrelationLink id={a.correlation_id} label={t('audit.correlation')} /> : null}
        </div>
      ),
    },
    { key: 'action', header: t('audit.action'), cell: (a) => L.auditAction(a.action) },
    { key: 'actor', header: t('audit.actor'), cell: (a) => <ActorName type={a.actor_type} name={a.actor_name} /> },
    {
      key: 'outcome',
      header: t('audit.outcome'),
      cell: (a) => (
        <Badge tone={a.outcome === 'SUCCESS' ? 'ok' : a.outcome === 'DENIED' ? 'warn' : 'crit'} title={a.outcome}>
          {outcomes[a.outcome] ?? humanize(a.outcome)}
        </Badge>
      ),
    },
    { key: 'reason', header: t('audit.reason'), cell: (a) => (a.reason ? <span className="text-sm"><SystemReason reason={a.reason} /></span> : <span className="text-muted-foreground">—</span>) },
  ];

  return (
    <Section flush title={t('audit.title')} description={t('audit.description')}>
      {audit.status === 'loading' ? (
        <Pad>
          <Loading rows={4} />
        </Pad>
      ) : null}
      {audit.status === 'error' ? (
        <Pad>
          <DataError error={audit.error} onRetry={audit.reload} title={t('audit.loadFailed')} />
        </Pad>
      ) : null}
      {audit.status === 'success' ? (
        <>
          <DataTable columns={columns} rows={audit.data.items} rowKey={(a) => a.id} empty={<EmptyState title={t('audit.empty')} icon={Inbox} />} />
          {audit.data.next_before || pages.length ? (
            <Pager
              canPrevious={pages.length > 0}
              canNext={Boolean(audit.data.next_before)}
              onPrevious={() => setPages((p) => p.slice(0, -1))}
              onNext={() => audit.data.next_before && setPages((p) => [...p, audit.data.next_before as string])}
              nextLabel={t('audit.older')}
            />
          ) : null}
        </>
      ) : null}
    </Section>
  );
}
